import React, { useMemo, useState, useEffect } from 'react';
import LineChartComponent from './LineChartComponent';

// Combined Trend Chart Component
const CombinedTrendChart = React.memo(({ isLoading, combinedChartData, combinedChartOptions }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  // Deep-clone the data to ensure stability across renders
  const stableCombinedChartData = useMemo(() => 
    JSON.parse(JSON.stringify(combinedChartData)), 
    [combinedChartData]
  );
  
  const stableCombinedChartOptions = useMemo(() => 
    JSON.parse(JSON.stringify(combinedChartOptions)), 
    [combinedChartOptions]
  );
  
  // Control visibility for smooth animation
  useEffect(() => {
    if (!isLoading) {
      // Small delay before showing to ensure smooth transition
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isLoading]);
  
  // Avoid re-renders when loading state changes
  const chartContent = useMemo(() => {
    if (isLoading) {
      return (
        <div className="h-full w-full flex items-center justify-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-white border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
        </div>
      );
    }
    
    return (
      <div 
        className={`transition-opacity duration-500 ease-in-out ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        style={{ height: '100%', width: '100%' }}
      >
        <LineChartComponent 
          key="combined-chart" 
          data={stableCombinedChartData} 
          options={stableCombinedChartOptions} 
        />
      </div>
    );
  }, [isLoading, isVisible, stableCombinedChartData, stableCombinedChartOptions]);
  
  return (
    <div className="bg-[#121212] rounded-xl shadow-lg p-4 border border-white/20 mb-6 w-full transform transition-all duration-300 hover:shadow-white/10">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-bold text-white">Revenue & Bookings Trend</h3>
        {isLoading && (
          <div className="animate-pulse text-white flex items-center">
            <span className="mr-2">Loading</span>
            <span className="flex space-x-1">
              <span className="h-1.5 w-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
              <span className="h-1.5 w-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
              <span className="h-1.5 w-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
            </span>
          </div>
        )}
      </div>
      <div className="h-64 relative overflow-hidden">
        {/* Preload container for better layout stability */}
        <div className="absolute inset-0 flex items-center justify-center">
          {chartContent}
        </div>
      </div>
    </div>
  );
});

export default CombinedTrendChart; 